import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AppProvider, useApp } from "./context/AppContext";

import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import ProfilePage from "./pages/ProfilePage";
import OrderHistoryPage from "./pages/OrderHistoryPage";

import AdminDashboard from "./pages/AdminDashboard";
import AdminOrders from "./pages/AdminOrders";
import AdminUsers from "./pages/AdminUsers";
import AdminAddProduct from "./pages/AdminAddProduct";
import AdminInventory from "./pages/AdminInventory";
import AdminAuditLogs from "./pages/AdminAuditLogs";
import AdminManageCategories from "./pages/AdminManageCategories";
import AdminContactQueries from "./pages/AdminContactQueries";
import AdminContactInfo from "./pages/AdminContactInfo";
import AdminPayments from "./pages/AdminPayments";
import AdminEmailTemplates from "./pages/AdminEmailTemplates";
import ContactPage from "./pages/ContactPage";

// ── Scroll to top on every route change ──
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

// ── Floating scroll-to-top button ──
// ── Floating scroll-to-top button ──
function ScrollTopButton() {
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    const onResize = () => setIsMobile(window.innerWidth < 768);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  if (!visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Scroll to top"
      style={{
        position: "fixed",
        // ADJUSTMENT: If mobile, move up to 85px to clear the bottom nav (usually 60-70px high)
        bottom: isMobile ? "90px" : "28px",
        right: "20px",
        zIndex: 9999,
        width: "44px",
        height: "44px",
        borderRadius: "50%",
        background: "linear-gradient(135deg, #2D8B4E, #4CAF72)",
        boxShadow: "0 6px 24px rgba(45,139,78,0.45)",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)", // Smoother transition
        animation: "fadeInUp 0.25s ease",
      }}
      onMouseEnter={(e) => {
        if (!isMobile) {
          e.currentTarget.style.transform = "translateY(-3px)";
          e.currentTarget.style.boxShadow = "0 10px 32px rgba(45,139,78,0.55)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 6px 24px rgba(45,139,78,0.45)";
      }}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="18 15 12 9 6 15" />
      </svg>
    </button>
  );
}

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useApp();
  if (loading) return null;
  return user ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useApp();
  if (loading) return null;
  return user?.role === "admin" ? children : <Navigate to="/" replace />;
};

function NotFound() {
  const navigate = useNavigate();
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg,#f0f9f0,#e8f5e9)",
      }}
    >
      <div style={{ fontSize: "5rem", marginBottom: "1rem" }}>🥤</div>
      <h1
        style={{
          fontSize: "2rem",
          fontWeight: 900,
          color: "#0f5230",
          marginBottom: "0.5rem",
        }}
      >
        404 — Page Not Found
      </h1>
      <p style={{ color: "#6b7280", marginBottom: "2rem" }}>
        The page you're looking for doesn't exist.
      </p>
      <button
        onClick={() => navigate("/")}
        style={{
          background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
          color: "white",
          border: "none",
          borderRadius: "100px",
          padding: "12px 32px",
          fontWeight: 700,
          fontSize: "15px",
          cursor: "pointer",
        }}
      >
        ← Back to Home
      </button>
    </div>
  );
}

function App() {
  return (
    <AppProvider>
      <Toaster
        position="top-right"
        gutter={8}
        toastOptions={{
          duration: 3000,
          style: {
            background: "#1c2b20",
            color: "#f0fdf4",
            borderRadius: "10px",
            fontSize: "13px",
            fontWeight: 600,
            fontFamily: '"Roboto", sans-serif',
            padding: "11px 15px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.22)",
            border: "1px solid rgba(255,255,255,0.06)",
            maxWidth: "300px",
          },
          success: {
            iconTheme: { primary: "#4CAF72", secondary: "#1c2b20" },
          },
          error: {
            style: {
              background: "#2b1c1c",
              border: "1px solid rgba(255,255,255,0.06)",
            },
            iconTheme: { primary: "#f87171", secondary: "#2b1c1c" },
          },
        }}
      />
      <Router>
        <ScrollToTop />
        <ScrollTopButton />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route
            path="/checkout"
            element={
              <ProtectedRoute>
                <CheckoutPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/order-success/:orderId"
            element={
              <ProtectedRoute>
                <OrderSuccessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <OrderHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <AdminRoute>
                <AdminOrders />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminUsers />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/products/add"
            element={
              <AdminRoute>
                <AdminAddProduct />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/inventory"
            element={
              <AdminRoute>
                <AdminInventory />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/categories"
            element={
              <AdminRoute>
                <AdminManageCategories />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/logs"
            element={
              <AdminRoute>
                <AdminAuditLogs />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/payments"
            element={
              <AdminRoute>
                <AdminPayments />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/contact-info"
            element={
              <AdminRoute>
                <AdminContactInfo />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/email-templates"
            element={
              <AdminRoute>
                <AdminEmailTemplates />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/contact"
            element={
              <AdminRoute>
                <AdminContactQueries />
              </AdminRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>

      {/* ── mobile bottom spacer — pushes content above fixed tab bar ── */}
      <div id="mob-bottom-spacer" />
      <style>{`
        #mob-bottom-spacer { display: none; }
        @media (max-width: 767px) {
          #mob-bottom-spacer { display: block; height: calc(60px + env(safe-area-inset-bottom)); }
        }
      `}</style>
    </AppProvider>
  );
}

export default App;
